import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Row,
  Col,
  Card,
  Statistic,
  DatePicker,
  Select,
  Spin,
  Typography,
  Table,
  Tag,
  Tabs,
  Empty,
  Tooltip as AntTooltip,
  Drawer,
  Image,
  Divider,
  Badge,
  Avatar,
  List,
  Progress,
} from "antd";
import {
  ShoppingCartOutlined,
  DollarOutlined,
  UserOutlined,
  FileTextOutlined,
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
  PieChartOutlined,
  MessageOutlined,
  HeartOutlined,
  LikeOutlined,
  TeamOutlined,
  ReloadOutlined,
  CrownOutlined,
  SafetyOutlined,
  StopOutlined,
  QuestionCircleOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  EyeOutlined,
  ShareAltOutlined,
  CommentOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import moment from "moment";
import {
  getOrderOverviewRequest,
  getRevenueByTimeRequest,
  getOrderStatusDistributionRequest,
  getTopProductsRequest,
  getOrdersByPaymentMethodRequest,
  getOrdersBySourceRequest,
  getUserGrowthRequest,
  getUserStatusDistributionRequest,
  getUserAuthMethodsRequest,
  getTopUsersByFollowersRequest,
  getUserEngagementRequest,
  getPostTrendsRequest,
  getReactionsSummaryRequest,
  getTopPostsRequest,
  getContentTypeBreakdownRequest,
  getChatStatsRequest,
  getPostDetailRequest,
} from "providers/DashboardStatsProvider/slice";
import "./styles.less";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// ========== RESPONSIVE HOOK ==========
const useResponsive = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return useMemo(() => ({
    isMobile: width < 576,
    isTablet: width >= 576 && width < 992,
    isDesktop: width >= 992,
    width,
    chartHeight: width < 576 ? 240 : width < 768 ? 280 : 350,
    chartHeightSm: width < 576 ? 220 : 300,
    pieRadius: width < 576 ? 70 : width < 768 ? 85 : 100,
    drawerWidth: width < 576 ? "100%" : width < 768 ? 420 : 560,
  }), [width]);
};

const COLORS = [
  "#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1",
  "#13c2c2", "#eb2f96", "#fa8c16", "#2f54eb", "#a0d911",
];

const STATUS_COLORS = {
  new: "#1890ff",
  shipped: "#faad14",
  success: "#52c41a",
  cancel: "#f5222d",
};

const STATUS_LABELS = {
  new: "Mới",
  shipped: "Đang giao",
  success: "Thành công",
  cancel: "Hủy",
};

const CONVERSATION_TYPE_LABELS = {
  USER_TO_USER: "Chat cá nhân",
  FONEHOUSE_TO_USER: "Hỗ trợ cửa hàng",
  VIETGO_TO_USER: "Hỗ trợ VietGo",
  CONSULATE_TO_USER: "Hỗ trợ Lãnh sự",
  GROUP: "Nhóm chat",
};

const PAYMENT_METHOD_LABELS = {
  Daibiki: "Thanh toán khi nhận hàng (代引き)",
  cash: "Tiền mặt",
  transfer: "Chuyển khoản",
};

const ORDER_SOURCE_LABELS = {
  APP: "Ứng dụng (App)",
  CMS: "Quản trị (CMS)",
  OTHER: "Nguồn khác",
};

const POST_TYPE_LABELS = {
  normal: "Bài viết thường",
  share: "Chia sẻ",
  avatar: "Cập nhật avatar",
  banner: "Cập nhật ảnh bìa",
  product: "Sản phẩm",
  store: "Cửa hàng",
  news: "Tin tức",
  visa: "Visa",
  group: "Nhóm",
  candidate_share: "Chia sẻ ứng viên",
  vote: "Bình chọn",
  travelPlace: "Địa điểm du lịch",
  health_community: "Cộng đồng y tế",
};

const REACTION_ICONS = {
  like: "👍",
  heart: "❤️",
  care: "🤗",
  laugh: "😂",
  wow: "😮",
  cry: "😢",
  angry: "😡",
};

// Tooltip giải thích cho từng mục thống kê
const STAT_TOOLTIPS = {
  // Tab 1: Doanh thu
  totalOrders: "Tổng số đơn hàng được tạo trong khoảng thời gian đã chọn, bao gồm tất cả trạng thái (mới, đang giao, thành công, hủy)",
  totalRevenue: "Tổng giá trị doanh thu từ tất cả đơn hàng (đơn vị: Yên Nhật). Bao gồm cả đơn thành công và chưa hoàn thành",
  avgOrderValue: "Giá trị trung bình của mỗi đơn hàng = Tổng doanh thu / Tổng số đơn",
  cancellationRate: "Tỷ lệ phần trăm đơn hàng bị hủy so với tổng số đơn. Trên 20% sẽ hiển thị cảnh báo đỏ",
  // Tab 2: Người dùng
  totalUsers: "Tổng số tài khoản đã đăng ký trên hệ thống, không phân biệt trạng thái",
  activeUsers: "Số tài khoản đang hoạt động bình thường, chưa bị khóa hoặc vô hiệu hóa",
  blockedUsers: "Số tài khoản bị admin khóa/chặn do vi phạm quy định hoặc lý do bảo mật",
  verifiedUsers: "Số tài khoản đã hoàn thành xác minh danh tính (KYC) trên hệ thống",
  postAuthors: "Số người dùng duy nhất (unique) đã đăng ít nhất 1 bài viết trong khoảng thời gian",
  totalPosts: "Tổng số bài viết đã được đăng thành công trong khoảng thời gian đã chọn",
  avgPostsPerUser: "Số bài viết trung bình mỗi người đăng = Tổng bài viết / Số người đăng bài",
  // Tab 3: Nội dung
  totalReactions: "Tổng số lượt reaction (like, heart, care, laugh, wow, cry, angry) trên tất cả bài viết trong khoảng thời gian",
  mostPopularReaction: "Loại reaction được người dùng sử dụng nhiều nhất trong khoảng thời gian đã chọn",
  totalMessages: "Tổng số tin nhắn được gửi qua hệ thống chat trong khoảng thời gian, bao gồm text, hình ảnh, video...",
};

const StatTitle = ({ title, tooltip }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
    {title}
    <AntTooltip title={tooltip} placement="topLeft">
      <QuestionCircleOutlined style={{ color: "#bbb", fontSize: 13, cursor: "help" }} />
    </AntTooltip>
  </span>
);

// Format tiền Yên Nhật
const formatCurrency = (val) => {
  if (!val && val !== 0) return "0";
  if (val >= 100000000) return `${(val / 100000000).toFixed(1)}億`;
  if (val >= 10000) return `${Math.round(val / 10000)}万`;
  return `¥${val.toLocaleString("ja-JP")}`;
};

const formatNumber = (val) => {
  if (!val && val !== 0) return "0";
  return val.toLocaleString("ja-JP");
};

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-custom-tooltip">
      <p className="tooltip-title">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color, margin: "4px 0" }}>
          {entry.name}:{" "}
          <strong>{formatter ? formatter(entry.value) : entry.value}</strong>
        </p>
      ))}
    </div>
  );
};

const renderCustomLabel = ({ name, percent }) => {
  if (percent < 0.05) return null;
  // On small screens, only show percentage
  if (window.innerWidth < 576) return `${(percent * 100).toFixed(0)}%`;
  return `${name} ${(percent * 100).toFixed(0)}%`;
};

// ========== TAB 1: DOANH THU ==========
const OrdersTab = ({ state, responsive }) => {
  const {
    orderOverview,
    revenueByTime,
    orderStatusDistribution,
    topProducts,
    ordersByPaymentMethod,
    ordersBySource,
  } = state;
  const { isMobile, chartHeight, chartHeightSm, pieRadius } = responsive;

  const overview = orderOverview || {};

  // Server returns: { period, totalRevenue, orderCount, avgOrderValue }
  const revenueChartData = (revenueByTime || []).map((item) => ({
    ...item,
    "Doanh thu": item.totalRevenue,
    "Số đơn": item.orderCount,
  }));

  // Server returns: { status, count, percentage }
  const statusPieData = (orderStatusDistribution || []).map((item) => ({
    name: STATUS_LABELS[item.status] || item.status,
    value: item.count,
    color: STATUS_COLORS[item.status] || "#999",
  }));

  // Server returns: { transactionType, count, totalRevenue, percentage }
  const paymentPieData = (ordersByPaymentMethod || []).map((item, idx) => ({
    name: PAYMENT_METHOD_LABELS[item.transactionType] || item.transactionType || "Khác",
    value: item.count,
    revenue: item.totalRevenue,
    color: COLORS[idx % COLORS.length],
  }));

  // Server returns: { orderFrom, count, totalRevenue, percentage }
  const sourcePieData = (ordersBySource || []).map((item, idx) => ({
    name: ORDER_SOURCE_LABELS[item.orderFrom] || item.orderFrom || "Khác",
    value: item.count,
    revenue: item.totalRevenue,
    color: COLORS[idx % COLORS.length],
  }));

  // Server returns: { productName, totalQuantity, totalRevenue, orderCount }
  const topProductColumns = [
    {
      title: "STT",
      key: "index",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Sản phẩm",
      dataIndex: "productName",
      key: "name",
      render: (v) => <strong>{v || "—"}</strong>,
    },
    {
      title: "Số lượng bán",
      dataIndex: "totalQuantity",
      key: "qty",
      sorter: (a, b) => a.totalQuantity - b.totalQuantity,
      render: (v) => <Tag color="blue">{formatNumber(v)}</Tag>,
    },
    {
      title: "Doanh thu",
      dataIndex: "totalRevenue",
      key: "revenue",
      sorter: (a, b) => a.totalRevenue - b.totalRevenue,
      render: (v) => (
        <span style={{ color: "#52c41a", fontWeight: 600 }}>
          ¥{formatNumber(v)}
        </span>
      ),
    },
  ];

  return (
    <>
      {/* Overview Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic
              title={<StatTitle title="Tổng đơn hàng" tooltip={STAT_TOOLTIPS.totalOrders} />}
              value={overview.totalOrders ?? "-"}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic
              title={<StatTitle title="Tổng doanh thu" tooltip={STAT_TOOLTIPS.totalRevenue} />}
              value={overview.totalRevenue ?? "-"}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#52c41a" }}
              formatter={(v) => formatCurrency(v)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic
              title={<StatTitle title="TB mỗi đơn" tooltip={STAT_TOOLTIPS.avgOrderValue} />}
              value={overview.avgOrderValue ?? "-"}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: "#722ed1" }}
              formatter={(v) => formatCurrency(v)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic
              title={<StatTitle title="Tỷ lệ hủy" tooltip={STAT_TOOLTIPS.cancellationRate} />}
              value={overview.cancellationRate ?? "-"}
              prefix={<FallOutlined />}
              valueStyle={{
                color:
                  (overview.cancellationRate || 0) > 20 ? "#f5222d" : "#faad14",
              }}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      {/* Revenue Chart */}
      <Card
        title={<><RiseOutlined /> Doanh thu theo thời gian</>}
        style={{ marginBottom: 16 }}
      >
        {revenueChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <ComposedChart data={revenueChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" fontSize={isMobile ? 9 : 11} angle={isMobile ? -45 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 60 : 30} />
              <YAxis
                yAxisId="left"
                tickFormatter={formatCurrency}
                width={isMobile ? 45 : 60}
                label={isMobile ? undefined : { value: "Doanh thu", angle: -90, position: "insideLeft", style: { fontSize: 12 } }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                width={isMobile ? 35 : 60}
                label={isMobile ? undefined : { value: "Số đơn", angle: 90, position: "insideRight", style: { fontSize: 12 } }}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    formatter={(v) =>
                      typeof v === "number" && v > 1000
                        ? `¥${v.toLocaleString("ja-JP")}`
                        : v
                    }
                  />
                }
              />
              <Legend />
              <Bar yAxisId="right" dataKey="Số đơn" fill="#1890ff" opacity={0.3} radius={[4, 4, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="Doanh thu" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Empty description="Không có dữ liệu" />
        )}
      </Card>

      {/* Pie Charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<><PieChartOutlined /> Trạng thái đơn hàng</>}>
            {statusPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <PieChart>
                  <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={pieRadius} dataKey="value" label={renderCustomLabel} labelLine={!isMobile}>
                    {statusPieData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={isMobile ? { fontSize: 12 } : undefined} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><PieChartOutlined /> Phương thức thanh toán</>}>
            {paymentPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <PieChart>
                  <Pie data={paymentPieData} cx="50%" cy="50%" outerRadius={pieRadius} dataKey="value" label={renderCustomLabel} labelLine={!isMobile}>
                    {paymentPieData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={isMobile ? { fontSize: 12 } : undefined} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
      </Row>

      {/* Source Bar */}
      <Card title={<><BarChartOutlined /> Nguồn đơn hàng</>} style={{ marginBottom: 16 }}>
        {sourcePieData.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeightSm}>
            <BarChart data={sourcePieData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={isMobile ? 10 : 12} angle={isMobile ? -30 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 50 : 30} />
              <YAxis width={isMobile ? 35 : 60} />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" name="Số đơn" fill="#1890ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (<Empty description="Không có dữ liệu" />)}
      </Card>

      {/* Top Products */}
      <Card title={<><CrownOutlined style={{ color: "#fa541c" }} /> Top sản phẩm bán chạy</>}>
        <Table
          columns={topProductColumns}
          dataSource={(topProducts || []).map((p, i) => ({ ...p, key: i }))}
          pagination={false}
          size="small"
          scroll={isMobile ? { x: 500 } : undefined}
          locale={{ emptyText: "Không có dữ liệu" }}
        />
      </Card>
    </>
  );
};

// ========== TAB 2: NGƯỜI DÙNG ==========
const UsersTab = ({ state, responsive }) => {
  const {
    userGrowth,
    userStatusDistribution,
    userAuthMethods,
    topUsersByFollowers,
    userEngagement,
  } = state;
  const { isMobile, chartHeight, chartHeightSm, pieRadius } = responsive;

  const statusDist = userStatusDistribution || {};
  const engagement = userEngagement || {};

  // Server returns: { period, newUsers, cumulativeTotal }
  const growthChartData = (userGrowth || []).map((item) => ({
    ...item,
    "User mới": item.newUsers,
    "Tổng cộng dồn": item.cumulativeTotal,
  }));

  // Server returns: { method, count, percentage }
  const authPieData = (userAuthMethods || []).map((item, idx) => ({
    name: item.method,
    value: item.count,
    color: COLORS[idx % COLORS.length],
  }));

  const statusPieData = statusDist
    ? [
        { name: "Active", value: statusDist.active || 0, color: "#52c41a" },
        { name: "Inactive", value: statusDist.inactive || 0, color: "#faad14" },
        { name: "Blocked", value: statusDist.blocked || 0, color: "#f5222d" },
        { name: "Disabled", value: statusDist.disabled || 0, color: "#999" },
      ].filter((d) => d.value > 0)
    : [];

  const topUsersColumns = [
    { title: "STT", key: "index", width: 50, render: (_, __, i) => i + 1 },
    { title: "Tên", dataIndex: "name", key: "name", render: (v) => <strong>{v || "—"}</strong> },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: "Followers", dataIndex: "followerCount", key: "followers",
      sorter: (a, b) => a.followerCount - b.followerCount,
      render: (v) => <Tag color="blue"><TeamOutlined /> {formatNumber(v)}</Tag>,
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic title={<StatTitle title="Tổng người dùng" tooltip={STAT_TOOLTIPS.totalUsers} />} value={statusDist.total ?? "-"} prefix={<UserOutlined />} valueStyle={{ color: "#1890ff" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic title={<StatTitle title="Active" tooltip={STAT_TOOLTIPS.activeUsers} />} value={statusDist.active ?? "-"} prefix={<SafetyOutlined />} valueStyle={{ color: "#52c41a" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic title={<StatTitle title="Blocked" tooltip={STAT_TOOLTIPS.blockedUsers} />} value={statusDist.blocked ?? "-"} prefix={<StopOutlined />} valueStyle={{ color: "#f5222d" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card overview-card">
            <Statistic title={<StatTitle title="Verified" tooltip={STAT_TOOLTIPS.verifiedUsers} />} value={statusDist.verified ?? "-"} prefix={<SafetyOutlined />} valueStyle={{ color: "#722ed1" }} />
          </Card>
        </Col>
      </Row>

      <Card title={<><RiseOutlined /> Tăng trưởng người dùng</>} style={{ marginBottom: 16 }}>
        {growthChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={growthChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" fontSize={isMobile ? 9 : 11} angle={isMobile ? -45 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 60 : 30} />
              <YAxis yAxisId="left" width={isMobile ? 35 : 60} label={isMobile ? undefined : { value: "User mới", angle: -90, position: "insideLeft", style: { fontSize: 12 } }} />
              <YAxis yAxisId="right" orientation="right" width={isMobile ? 35 : 60} label={isMobile ? undefined : { value: "Tổng cộng dồn", angle: 90, position: "insideRight", style: { fontSize: 12 } }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="User mới" stroke="#1890ff" fill="#1890ff" fillOpacity={0.2} />
              <Line yAxisId="right" type="monotone" dataKey="Tổng cộng dồn" stroke="#52c41a" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (<Empty description="Không có dữ liệu" />)}
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<><PieChartOutlined /> Phương thức đăng nhập</>}>
            {authPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <PieChart>
                  <Pie data={authPieData} cx="50%" cy="50%" outerRadius={pieRadius} dataKey="value" label={renderCustomLabel} labelLine={!isMobile}>
                    {authPieData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={isMobile ? { fontSize: 12 } : undefined} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><PieChartOutlined /> Trạng thái tài khoản</>}>
            {statusPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <PieChart>
                  <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={pieRadius} dataKey="value" label={renderCustomLabel} labelLine={!isMobile}>
                    {statusPieData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={isMobile ? { fontSize: 12 } : undefined} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
      </Row>

      {engagement && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card className="stat-card">
              <Statistic title={<StatTitle title="Người đăng bài" tooltip={STAT_TOOLTIPS.postAuthors} />} value={engagement.totalPostAuthors ?? "-"} prefix={<FileTextOutlined />} valueStyle={{ color: "#1890ff" }} />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="stat-card">
              <Statistic title={<StatTitle title="Tổng bài viết" tooltip={STAT_TOOLTIPS.totalPosts} />} value={engagement.totalPosts ?? "-"} prefix={<FileTextOutlined />} valueStyle={{ color: "#722ed1" }} />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="stat-card">
              <Statistic title={<StatTitle title="TB bài/người" tooltip={STAT_TOOLTIPS.avgPostsPerUser} />} value={engagement.avgPostsPerUser ?? "-"} prefix={<BarChartOutlined />} valueStyle={{ color: "#52c41a" }} precision={1} />
            </Card>
          </Col>
        </Row>
      )}

      <Card title={<><CrownOutlined style={{ color: "#fa541c" }} /> Top người dùng nhiều followers nhất</>}>
        <Table
          columns={topUsersColumns}
          dataSource={(topUsersByFollowers || []).map((u, i) => ({ ...u, key: u._id || i }))}
          pagination={false}
          size="small"
          scroll={isMobile ? { x: 500 } : undefined}
          locale={{ emptyText: "Không có dữ liệu" }}
        />
      </Card>
    </>
  );
};

// Helper: render nội dung bài viết (text hoặc media indicator)
const PostContentCell = ({ record }) => {
  const { content, type, imageCount, videoCount } = record;
  const typeLabel = POST_TYPE_LABELS[type] || type;
  const mediaTags = [];
  if (imageCount > 0) mediaTags.push(<Tag key="img" color="blue" style={{ fontSize: 11 }}><PictureOutlined /> {imageCount} ảnh</Tag>);
  if (videoCount > 0) mediaTags.push(<Tag key="vid" color="purple" style={{ fontSize: 11 }}><VideoCameraOutlined /> {videoCount} video</Tag>);

  if (content && content.trim()) {
    return (
      <div>
        <div style={{ marginBottom: mediaTags.length > 0 ? 4 : 0 }}>{content}</div>
        {mediaTags.length > 0 && <div>{mediaTags}</div>}
      </div>
    );
  }

  // Không có text -> hiển thị media tags hoặc loại bài viết
  if (mediaTags.length > 0) {
    return (
      <div>
        <div>{mediaTags}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>{typeLabel}</Text>
      </div>
    );
  }

  return (
    <div>
      <Tag color="default">{typeLabel}</Tag>
      <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>(không có nội dung text)</Text>
    </div>
  );
};

// Drawer chi tiết bài viết
const PostDetailDrawer = ({ visible, onClose, postDetail, loading, drawerWidth }) => {
  const detail = postDetail || {};
  const reactions = detail
    ? { like: detail.like, heart: detail.heart, care: detail.care, laugh: detail.laugh, wow: detail.wow, cry: detail.cry, angry: detail.angry }
    : {};
  const totalReactions = detail.totalReactions || 0;

  return (
    <Drawer
      title="Chi tiết bài viết"
      placement="right"
      width={drawerWidth || 560}
      onClose={onClose}
      visible={visible}
      className="post-detail-drawer-container"
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
      ) : detail._id ? (
        <div className="post-detail-drawer">
          {/* Author info */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <Avatar size={44} src={detail.ownerAvatar} icon={<UserOutlined />} />
            <div>
              <Text strong style={{ fontSize: 15 }}>{detail.ownerName || "Ẩn danh"}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ClockCircleOutlined /> {detail.createdAt ? moment(detail.createdAt).format("HH:mm DD/MM/YYYY") : "—"}
                {" · "}
                <Tag color="geekblue" style={{ fontSize: 11, marginLeft: 4 }}>{POST_TYPE_LABELS[detail.type] || detail.type}</Tag>
              </Text>
            </div>
          </div>

          {/* Content */}
          {detail.content && (
            <div style={{ background: "#f9f9f9", padding: 12, borderRadius: 8, marginBottom: 16, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
              {detail.content}
            </div>
          )}

          {/* Attachments */}
          {detail.attachments && detail.attachments.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ marginBottom: 8, display: "block" }}>
                <PictureOutlined /> Media ({detail.attachments.length})
              </Text>
              <Image.PreviewGroup>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {detail.attachments.map((att, i) => {
                    if (att.type && att.type.startsWith("video")) {
                      return (
                        <div key={i} style={{ width: 120, height: 90, background: "#000", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                          <VideoCameraOutlined style={{ color: "#fff", fontSize: 28 }} />
                          <Tag color="purple" style={{ position: "absolute", bottom: 4, right: 4, fontSize: 10, margin: 0 }}>Video</Tag>
                        </div>
                      );
                    }
                    return (
                      <Image
                        key={i}
                        src={att.thumb || att.url}
                        width={120}
                        height={90}
                        style={{ objectFit: "cover", borderRadius: 6 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQABNjN9GQAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAA0lEQVQI12P4//8/AwAI/AL+5gz4aAAAAABJRU5ErkJggg=="
                      />
                    );
                  })}
                </div>
              </Image.PreviewGroup>
            </div>
          )}

          <Divider />

          {/* Reactions breakdown */}
          <Text strong style={{ marginBottom: 8, display: "block" }}><HeartOutlined /> Reactions ({formatNumber(totalReactions)})</Text>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {Object.entries(reactions).map(([key, val]) => (
              val > 0 ? (
                <Tag key={key} style={{ fontSize: 13, padding: "4px 10px" }}>
                  {REACTION_ICONS[key] || key} {formatNumber(val)}
                </Tag>
              ) : null
            ))}
            {totalReactions === 0 && <Text type="secondary">Chưa có reaction</Text>}
          </div>

          {/* Stats row */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic title={<><CommentOutlined /> Comments</>} value={detail.commentCount || 0} valueStyle={{ fontSize: 18 }} />
            </Col>
            <Col span={8}>
              <Statistic title={<><ShareAltOutlined /> Shares</>} value={detail.shareCount || 0} valueStyle={{ fontSize: 18 }} />
            </Col>
            <Col span={8}>
              <Statistic title={<><HeartOutlined /> Reactions</>} value={totalReactions} valueStyle={{ fontSize: 18, color: "#f5222d" }} />
            </Col>
          </Row>

          <Divider />

          {/* Comments list */}
          <Text strong style={{ marginBottom: 8, display: "block" }}>
            <CommentOutlined /> Bình luận gần nhất ({detail.comments?.length || 0})
          </Text>
          {detail.comments && detail.comments.length > 0 ? (
            <List
              itemLayout="horizontal"
              dataSource={detail.comments}
              renderItem={(comment) => (
                <List.Item style={{ padding: "8px 0" }}>
                  <List.Item.Meta
                    avatar={<Avatar size={32} src={comment.ownerAvatar} icon={<UserOutlined />} />}
                    title={
                      <span>
                        <Text strong style={{ fontSize: 13 }}>{comment.ownerName || "Ẩn danh"}</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                          {comment.createdAt ? moment(comment.createdAt).format("HH:mm DD/MM") : ""}
                        </Text>
                      </span>
                    }
                    description={<Text style={{ fontSize: 13 }}>{comment.content || <Text type="secondary">(media)</Text>}</Text>}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="Chưa có bình luận" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      ) : (
        <Empty description="Không tìm thấy bài viết" />
      )}
    </Drawer>
  );
};

// ========== TAB 3: NỘI DUNG ==========
const SocialTab = ({ state, dispatch, responsive }) => {
  const { postTrends, reactionsSummary, topPosts, contentTypeBreakdown, chatStats, postDetail, postDetailLoading } = state;
  const { isMobile, chartHeight, chartHeightSm, pieRadius, drawerWidth } = responsive;
  const [drawerVisible, setDrawerVisible] = useState(false);

  const reactions = reactionsSummary || {};
  const chat = chatStats || {};

  const handleViewPost = (postId) => {
    dispatch(getPostDetailRequest({ id: postId }));
    setDrawerVisible(true);
  };

  // Server returns: { period, postCount, avgReactions, avgComments, avgShares }
  const postTrendsData = (postTrends || []).map((item) => ({
    ...item,
    "Số bài": item.postCount,
    "TB reactions": item.avgReactions,
    "TB comments": item.avgComments,
  }));

  // Server returns: { totalReactions, byType: { like, heart, ... }, mostPopularReaction: { type, count } }
  const reactionBarData = reactions.byType
    ? Object.entries(reactions.byType).map(([key, val]) => ({
        name: `${REACTION_ICONS[key] || ""} ${key}`,
        value: val,
      }))
    : [];

  // Server returns: { type, count, percentage, avgReactions }
  const contentPieData = (contentTypeBreakdown || []).map((item, idx) => ({
    name: POST_TYPE_LABELS[item.type] || item.type || "Khác",
    value: item.count,
    color: COLORS[idx % COLORS.length],
  }));

  // Server returns: messagesByDay: [{ date, messageCount }]
  const dailyMessageData = chat.messagesByDay
    ? (chat.messagesByDay || []).map((item) => ({ ...item, "Tin nhắn": item.messageCount }))
    : [];

  // Server returns: conversationTypeCounts: [{ type, count }]
  const conversationTypeData = chat.conversationTypeCounts
    ? (chat.conversationTypeCounts || []).map((item, idx) => ({
        name: CONVERSATION_TYPE_LABELS[item.type] || item.type || "Khác",
        value: item.count,
        color: COLORS[idx % COLORS.length],
      }))
    : [];

  const topPostColumns = [
    { title: "STT", key: "index", width: 50, render: (_, __, i) => i + 1 },
    {
      title: "Nội dung",
      key: "content",
      ellipsis: true,
      render: (_, record) => <PostContentCell record={record} />,
    },
    { title: "Tác giả", dataIndex: "ownerName", key: "owner", width: 150, render: (v) => <strong>{v || "—"}</strong> },
    { title: "Reactions", dataIndex: "totalReactions", key: "reactions", width: 100, sorter: (a, b) => a.totalReactions - b.totalReactions, render: (v) => <Tag color="red">{formatNumber(v)}</Tag> },
    { title: "Comments", dataIndex: "commentCount", key: "comments", width: 100, sorter: (a, b) => (a.commentCount || 0) - (b.commentCount || 0), render: (v) => <Tag color="blue">{formatNumber(v || 0)}</Tag> },
    { title: "Ngày đăng", dataIndex: "createdAt", key: "date", width: 120, render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "—") },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record) => (
        <AntTooltip title="Xem chi tiết">
          <a onClick={() => handleViewPost(record._id)} style={{ color: "#1890ff" }}>
            <EyeOutlined style={{ fontSize: 16 }} />
          </a>
        </AntTooltip>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card className="stat-card overview-card">
            <Statistic title={<StatTitle title="Tổng reactions" tooltip={STAT_TOOLTIPS.totalReactions} />} value={reactions.totalReactions ?? "-"} prefix={<HeartOutlined />} valueStyle={{ color: "#f5222d" }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card overview-card">
            <Statistic
              title={<StatTitle title="Reaction phổ biến nhất" tooltip={STAT_TOOLTIPS.mostPopularReaction} />}
              value={
                reactions.mostPopularReaction
                  ? `${REACTION_ICONS[reactions.mostPopularReaction.type] || ""} ${reactions.mostPopularReaction.type}`
                  : "-"
              }
              prefix={<LikeOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card overview-card">
            <Statistic title={<StatTitle title="Tổng tin nhắn" tooltip={STAT_TOOLTIPS.totalMessages} />} value={chat.totalMessages ?? "-"} prefix={<MessageOutlined />} valueStyle={{ color: "#1890ff" }} />
          </Card>
        </Col>
      </Row>

      <Card title={<><RiseOutlined /> Xu hướng bài viết</>} style={{ marginBottom: 16 }}>
        {postTrendsData.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <ComposedChart data={postTrendsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" fontSize={isMobile ? 9 : 11} angle={isMobile ? -45 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 60 : 30} />
              <YAxis yAxisId="left" width={isMobile ? 30 : 60} label={isMobile ? undefined : { value: "Số bài", angle: -90, position: "insideLeft", style: { fontSize: 12 } }} />
              <YAxis yAxisId="right" orientation="right" width={isMobile ? 30 : 60} label={isMobile ? undefined : { value: "TB tương tác", angle: 90, position: "insideRight", style: { fontSize: 12 } }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar yAxisId="left" dataKey="Số bài" fill="#1890ff" radius={[4, 4, 0, 0]} opacity={0.6} />
              <Line yAxisId="right" type="monotone" dataKey="TB reactions" stroke="#f5222d" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="TB comments" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (<Empty description="Không có dữ liệu" />)}
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<><HeartOutlined /> Reactions theo loại</>}>
            {reactionBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <BarChart data={reactionBarData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={isMobile ? 70 : 100} fontSize={isMobile ? 11 : 13} />
                  <Tooltip />
                  <Bar dataKey="value" name="Số lượng" radius={[0, 4, 4, 0]}>
                    {reactionBarData.map((_, idx) => (<Cell key={idx} fill={COLORS[idx % COLORS.length]} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><PieChartOutlined /> Loại nội dung</>}>
            {contentPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <PieChart>
                  <Pie data={contentPieData} cx="50%" cy="50%" outerRadius={pieRadius} dataKey="value" label={renderCustomLabel} labelLine={!isMobile}>
                    {contentPieData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={isMobile ? { fontSize: 12 } : undefined} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
      </Row>

      <Card title={<><CrownOutlined style={{ color: "#fa541c" }} /> Top bài viết tương tác cao</>} style={{ marginBottom: 16 }}>
        <Table
          columns={topPostColumns}
          dataSource={(topPosts || []).map((p, i) => ({ ...p, key: i }))}
          pagination={false}
          size="small"
          scroll={isMobile ? { x: 700 } : undefined}
          locale={{ emptyText: "Không có dữ liệu" }}
          onRow={(record) => ({
            onClick: () => handleViewPost(record._id),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title={<><MessageOutlined /> Tin nhắn theo ngày</>}>
            {dailyMessageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <BarChart data={dailyMessageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={isMobile ? 9 : 11} angle={isMobile ? -45 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 60 : 30} />
                  <YAxis width={isMobile ? 35 : 60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Tin nhắn" fill="#1890ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<><PieChartOutlined /> Loại cuộc hội thoại</>}>
            {conversationTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={chartHeightSm}>
                <PieChart>
                  <Pie data={conversationTypeData} cx="50%" cy="50%" outerRadius={pieRadius} dataKey="value" label={renderCustomLabel} labelLine={!isMobile}>
                    {conversationTypeData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={isMobile ? { fontSize: 12 } : undefined} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<Empty description="Không có dữ liệu" />)}
          </Card>
        </Col>
      </Row>

      <PostDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        postDetail={postDetail}
        loading={postDetailLoading}
        drawerWidth={drawerWidth}
      />
    </>
  );
};

// ========== MAIN COMPONENT ==========
const DashboardStats = () => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.dashboardStats);
  const { isLoading } = state;
  const responsive = useResponsive();
  const { isMobile } = responsive;

  const [activeTab, setActiveTab] = useState("orders");
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);
  const [granularity, setGranularity] = useState("daily");

  const fetchOrdersData = useCallback(() => {
    const startDate = dateRange[0].format("YYYY-MM-DD");
    const endDate = dateRange[1].format("YYYY-MM-DD");
    dispatch(getOrderOverviewRequest({ startDate, endDate }));
    dispatch(getRevenueByTimeRequest({ startDate, endDate, granularity }));
    dispatch(getOrderStatusDistributionRequest({ startDate, endDate }));
    dispatch(getTopProductsRequest({ startDate, endDate, limit: 10 }));
    dispatch(getOrdersByPaymentMethodRequest({ startDate, endDate }));
    dispatch(getOrdersBySourceRequest({ startDate, endDate }));
  }, [dispatch, dateRange, granularity]);

  const fetchUsersData = useCallback(() => {
    const startDate = dateRange[0].format("YYYY-MM-DD");
    const endDate = dateRange[1].format("YYYY-MM-DD");
    dispatch(getUserGrowthRequest({ startDate, endDate, granularity }));
    dispatch(getUserStatusDistributionRequest());
    dispatch(getUserAuthMethodsRequest());
    dispatch(getTopUsersByFollowersRequest({ limit: 10 }));
    dispatch(getUserEngagementRequest({ startDate, endDate }));
  }, [dispatch, dateRange, granularity]);

  const fetchSocialData = useCallback(() => {
    const startDate = dateRange[0].format("YYYY-MM-DD");
    const endDate = dateRange[1].format("YYYY-MM-DD");
    dispatch(getPostTrendsRequest({ startDate, endDate, granularity }));
    dispatch(getReactionsSummaryRequest({ startDate, endDate }));
    dispatch(getTopPostsRequest({ startDate, endDate, limit: 10 }));
    dispatch(getContentTypeBreakdownRequest({ startDate, endDate }));
    dispatch(getChatStatsRequest({ startDate, endDate }));
  }, [dispatch, dateRange, granularity]);

  useEffect(() => {
    if (activeTab === "orders") fetchOrdersData();
    else if (activeTab === "users") fetchUsersData();
    else if (activeTab === "social") fetchSocialData();
  }, [activeTab, fetchOrdersData, fetchUsersData, fetchSocialData]);

  const onDateRangeChange = (dates) => {
    if (dates && dates.length === 2) setDateRange(dates);
  };

  const handleRefresh = () => {
    if (activeTab === "orders") fetchOrdersData();
    else if (activeTab === "users") fetchUsersData();
    else if (activeTab === "social") fetchSocialData();
  };

  return (
    <div className="dashboard-stats-wrapper">
      <Title level={3}>
        <BarChartOutlined /> Thống kê tổng hợp
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
        Thống kê chi tiết về đơn hàng, doanh thu, người dùng và nội dung trên nền tảng.
      </Text>

      <Card className="filter-bar">
        <Row gutter={[12, 12]} align="middle" wrap>
          <Col xs={24} sm={24} md="auto">
            {!isMobile && <span style={{ marginRight: 8, fontWeight: 500 }}>Khoảng thời gian:</span>}
            <RangePicker
              value={dateRange}
              onChange={onDateRangeChange}
              format="DD/MM/YYYY"
              placeholder={["Từ ngày", "Đến ngày"]}
              style={isMobile ? { width: "100%" } : undefined}
              size={isMobile ? "middle" : "middle"}
            />
          </Col>
          <Col xs={12} sm={12} md="auto">
            {!isMobile && <span style={{ marginRight: 8, fontWeight: 500 }}>Xem theo:</span>}
            <Select value={granularity} onChange={setGranularity} style={{ width: isMobile ? "100%" : 130 }}>
              <Option value="daily">Theo ngày</Option>
              <Option value="weekly">Theo tuần</Option>
              <Option value="monthly">Theo tháng</Option>
            </Select>
          </Col>
          <Col xs={12} sm={12} md="auto">
            <a onClick={handleRefresh} style={{ cursor: "pointer" }}>
              <ReloadOutlined /> Làm mới
            </a>
          </Col>
        </Row>
      </Card>

      <Spin spinning={isLoading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane tab={<span><ShoppingCartOutlined />{!isMobile && " Doanh thu"}{isMobile && " DT"}</span>} key="orders">
            <OrdersTab state={state} responsive={responsive} />
          </TabPane>
          <TabPane tab={<span><UserOutlined />{!isMobile && " Người dùng"}{isMobile && " ND"}</span>} key="users">
            <UsersTab state={state} responsive={responsive} />
          </TabPane>
          <TabPane tab={<span><FileTextOutlined />{!isMobile && " Nội dung"}{isMobile && " NL"}</span>} key="social">
            <SocialTab state={state} dispatch={dispatch} responsive={responsive} />
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default DashboardStats;
